import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../api/client';
import { BroadcastMessage, BroadcastSegmentType, PlanItem } from '../../types';
import { Megaphone, Send, CheckCircle2, Users, RefreshCw, Loader2, Target } from 'lucide-react';

const FACETS: { k: BroadcastSegmentType; label: string }[] = [
  { k: 'all', label: 'Everyone' },
  { k: 'plan', label: 'By plan' },
  { k: 'status', label: 'By subscription' },
  { k: 'region', label: 'By camera region' },
];
const STATUSES = [
  { value: 'active', label: 'Active subscribers' },
  { value: 'past_due', label: 'Past due' },
  { value: 'trialing', label: 'On a trial' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'none', label: 'No subscription' },
];
const REGIONS = [
  { value: 'china', label: 'China cloud — cameras with no cloud recordings' },
  { value: 'us', label: 'US cloud' },
  { value: 'eu', label: 'EU cloud' },
];

// A human sentence for the chosen audience, for the summary + confirm dialog.
const audienceLabel = (type: BroadcastSegmentType, value: string, plans: PlanItem[]): string => {
  if (type === 'all') return 'every customer with the app installed';
  if (type === 'plan') {
    if (value === 'none') return 'customers with no active plan';
    const p = plans.find((x) => x.slug === value);
    return `customers on the ${p?.name ?? value} plan`;
  }
  if (type === 'status') {
    const s = STATUSES.find((x) => x.value === value);
    return value === 'none' ? 'customers with no subscription' : `${(s?.label ?? value).toLowerCase()}`;
  }
  if (type === 'region') {
    const r = REGIONS.find((x) => x.value === value);
    return `owners of ${(r?.label ?? value).split(' — ')[0]} cameras`;
  }
  return 'the selected customers';
};

// Turn a stored audience code ('plan:silver') into a readable chip label.
const historyAudience = (a: string, plans: PlanItem[]): string => {
  if (!a || a === 'all') return 'Everyone';
  const [t, v] = a.split(':');
  if (t === 'plan') return v === 'none' ? 'No plan' : (plans.find((p) => p.slug === v)?.name ?? v);
  if (t === 'status') return v === 'none' ? 'No subscription' : (STATUSES.find((s) => s.value === v)?.label ?? v);
  if (t === 'region') return (REGIONS.find((r) => r.value === v)?.label.split(' — ')[0] ?? v);
  return a;
};

export const BroadcastPage: React.FC = () => {
  const [broadcasts, setBroadcasts] = useState<BroadcastMessage[]>([]);
  const [plans, setPlans] = useState<PlanItem[]>([]);
  const [reach, setReach] = useState<number | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [segType, setSegType] = useState<BroadcastSegmentType>('all');
  const [segValue, setSegValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, pl] = await Promise.all([api.getBroadcasts(), api.getPlans().catch(() => [])]);
      setBroadcasts(list);
      setPlans(pl);
    } catch (err) {
      console.error('Failed to load broadcasts', err);
      setError('Could not load broadcasts. Check that you are signed in as an admin.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live reach for the current segment.
  useEffect(() => {
    let cancelled = false;
    setReach(null);
    api.getBroadcastAudience({ type: segType, value: segType === 'all' ? '' : segValue })
      .then((n) => { if (!cancelled) setReach(n); })
      .catch(() => { if (!cancelled) setReach(0); });
    return () => { cancelled = true; };
  }, [segType, segValue]);

  const chooseFacet = (k: BroadcastSegmentType) => {
    setSegType(k);
    if (k === 'plan') setSegValue(plans[0]?.slug ?? 'none');
    else if (k === 'status') setSegValue('active');
    else if (k === 'region') setSegValue('china');
    else setSegValue('');
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    const n = reach ?? 0;
    const who = audienceLabel(segType, segValue, plans);
    if (n === 0) { setError(`That audience (${who}) has no reachable devices right now.`); return; }
    if (!window.confirm(
      `Push this to ${n} device${n === 1 ? '' : 's'} — ${who}. This can't be recalled.`,
    )) return;
    setIsSending(true);
    setError(null);
    try {
      const sent = await api.sendBroadcast(title.trim(), body.trim(), { type: segType, value: segType === 'all' ? '' : segValue });
      setTitle('');
      setBody('');
      setSuccess(`Sent to ${sent.recipientCount} device${sent.recipientCount === 1 ? '' : 's'} — ${who}.`);
      setTimeout(() => setSuccess(null), 6000);
      load();
    } catch (err) {
      setError((err as { message?: string })?.message || 'Failed to send broadcast.');
    } finally {
      setIsSending(false);
    }
  };

  const facetBtn = (active: boolean): React.CSSProperties => ({
    fontSize: 12.5, padding: '7px 13px', borderRadius: 8, cursor: 'pointer', whiteSpace: 'nowrap',
    border: `1px solid ${active ? 'var(--primary-light)' : 'var(--border-dim)'}`,
    background: active ? 'rgba(56,189,248,0.14)' : 'transparent',
    color: active ? 'var(--primary-light)' : 'var(--text-muted)', fontWeight: active ? 600 : 400,
  });

  return (
    <div>
      <div style={{ background: 'var(--bg-surface-card)', border: '1px solid var(--border-dim)', borderRadius: 'var(--radius-lg)', padding: '16px 20px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
          <Megaphone size={18} color="var(--primary-light)" />
          <h2 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-main)' }}>Customer Broadcasts</h2>
        </div>
        <p style={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>
          Push a notification to every customer, or to a chosen group — by plan, subscription status, or the cloud region their cameras are on.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(440px, 1fr))', gap: '20px', alignItems: 'start' }}>
        {/* Compose */}
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: '16px' }}>Compose broadcast</h3>

          {success && (
            <div style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.35)', color: 'var(--success-text)', padding: '10px 14px', borderRadius: 'var(--radius-md)', marginBottom: '16px', fontSize: '12.5px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CheckCircle2 size={16} /> <span>{success}</span>
            </div>
          )}
          {error && (
            <div style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.35)', color: 'var(--danger-text)', padding: '10px 14px', borderRadius: 'var(--radius-md)', marginBottom: '16px', fontSize: '12.5px' }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSend}>
            {/* Audience */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
              <Target size={13} /> Audience
            </label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
              {FACETS.map((f) => (
                <button type="button" key={f.k} style={facetBtn(segType === f.k)} onClick={() => chooseFacet(f.k)}>
                  {f.label}
                </button>
              ))}
            </div>

            {segType !== 'all' && (
              <select className="select" style={{ width: '100%', marginBottom: 12 }} value={segValue} onChange={(e) => setSegValue(e.target.value)}>
                {segType === 'plan' && [
                  ...plans.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>),
                  <option key="none" value="none">No active plan</option>,
                ]}
                {segType === 'status' && STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                {segType === 'region' && REGIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            )}

            {/* Reach */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.22)', borderRadius: 'var(--radius-md)', padding: '11px 14px', marginBottom: 18 }}>
              <Users size={16} color="var(--primary-light)" style={{ flexShrink: 0 }} />
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                {reach === null ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Loader2 size={13} className="spin" /> Counting…</span>
                ) : (
                  <>Reaches <strong style={{ color: 'var(--text-main)', fontSize: 14 }}>{reach}</strong> device{reach === 1 ? '' : 's'} — {audienceLabel(segType, segValue, plans)}.</>
                )}
              </div>
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Notification title</label>
              <input type="text" className="input" style={{ width: '100%' }} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Scheduled maintenance tonight" maxLength={200} required />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Message</label>
              <textarea className="input" style={{ width: '100%', minHeight: '110px', resize: 'vertical' }} value={body} onChange={(e) => setBody(e.target.value)} placeholder="What's happening, the timeline, and what customers should do…" maxLength={2000} required />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: '11px', color: 'var(--text-dim)' }}>Delivered as a push notification.</span>
              <button type="submit" className="btn btn-primary btn-sm" disabled={isSending || reach === 0}>
                {isSending ? <Loader2 size={14} className="spin" /> : <Send size={14} />}
                <span>{isSending ? 'Sending…' : `Send to ${reach ?? '…'}`}</span>
              </button>
            </div>
          </form>
        </div>

        {/* History */}
        <div className="card">
          <div className="card-header" style={{ marginBottom: '16px' }}>
            <h3 className="card-title">Broadcast history</h3>
            <button onClick={load} disabled={loading} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', padding: '4px 10px', borderRadius: '6px', cursor: loading ? 'default' : 'pointer', border: '1px solid var(--border-dim)', background: 'transparent', color: 'var(--text-muted)' }}>
              <RefreshCw size={12} className={loading ? 'spin' : ''} /> Refresh
            </button>
          </div>

          {loading ? (
            <div style={{ padding: '16px', color: 'var(--text-dim)', fontSize: '13px' }}>Loading…</div>
          ) : broadcasts.length === 0 ? (
            <div style={{ padding: '16px', color: 'var(--text-dim)', fontSize: '13px' }}>No broadcasts sent yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {broadcasts.map((msg) => (
                <div key={msg.id} style={{ background: 'rgba(15, 23, 42, 0.6)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '12px 16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', marginBottom: '4px' }}>
                    <strong style={{ fontSize: '13.5px', color: 'var(--text-main)' }}>{msg.title}</strong>
                    <span style={{ flexShrink: 0, fontSize: '11px', color: 'var(--text-dim)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Users size={12} /> {msg.recipientCount}
                    </span>
                  </div>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px', whiteSpace: 'pre-wrap' }}>{msg.body}</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 10.5, fontWeight: 600, padding: '1px 8px', borderRadius: 999, background: 'rgba(56,189,248,0.12)', color: 'var(--primary-light)', border: '1px solid rgba(56,189,248,0.25)' }}>
                      {historyAudience(msg.audience, plans)}
                    </span>
                    <span style={{ fontSize: '11px', color: 'var(--text-dim)' }}>
                      {new Date(msg.createdAt).toLocaleString()}{msg.sentByEmail ? ` · by ${msg.sentByEmail}` : ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
